/**
 * Library
 */
import { Builder } from 'builder-pattern';

/**
 * VO
 */
import KeyFlowVO from 'commons/vo/KeyFlowVO';
import ConfigVO from 'commons/vo/ConfigVO';
import {
  Auth,
  DEFAULT_SYMBOLIC_PATH,
  WKBLVideoContentsType,
} from 'commons/constants/KeyFlowConstants';

/**
 * Utils
 */
import {
  AssetUrlBuilder,
  formattedDateString,
  assetWithToken,
  assetWithOutToken,
} from 'commons/utils/commonUtil';
import TimeCode from 'commons/utils/TimeCode';
import HostFactory from 'commons/handler/HostFactory';
import {
  LowresRelativeURLBuilder,
  ScriptThumbnailRelativeURLBuilder,
  SkimRelativeURLBuilder,
  ThumbnailRelativeURLBuilder,
} from 'commons/utils/MediaURLBuilder';

import { File, STORAGE_URL, SystemFolder } from 'commons/constants/KeyFlowConstants';

import defaultThumbnail from 'asset/thumbnail/thumb_default.jpg';
import { API } from 'commons/constants/KeyFlowAPI';

type WkblVideoVOProps = {
  ID: number;
  REF_ID: number;
  TYPE: string;
  SUB_TYPE: string;
  TITLE: string;
  CATEGORY_ID: number;
  MEDIA_FORMAT: string;
  MEDIA_FPS: number;
  MEDIA_FILE_SIZE: number;
  DURATION_OF_CLIP: number;
  SEASON: string;
  GAME_TYPE: number;
  GAME_DATE: string;
  VIDEO_TYPE: string;
  TEAM_1: number;
  TEAM_1_NAME: string;
  TEAM_1_COLOR: string;
  TEAM_1_LOGO: string;
  TEAM_2: number;
  TEAM_2_NAME: string;
  TEAM_2_COLOR: string;
  TEAM_2_LOGO: string;
  IN_POINT: number;
  OUT_POINT: number;
  THUMBNAIL_PATH: string;
  LOWRES_PATH: string;
  SKIM_PATH: string;
  DATE_OF_CREATED: string;
  DATE_OF_MODIFIED: string;
  INTERVIEW_PARTIAL_FILENAME: string;
  HIGHLIGHT_PARTIAL_FILENAME: string;
};

class WkblVideoVO extends KeyFlowVO {
  id: number;
  refId: number;
  type: string;
  subType: string;
  title: string;
  categoryId: number;
  mediaFormat: string;
  mediaFps: number;
  mediaFileSize: number;
  durationOfClip: number;
  season: string;
  gameType: number;
  gameDate: string;
  videoType: string;
  team1: number;
  team1Name: string;
  team1Color: string;
  team1Logo: string;
  team2: number;
  team2Name: string;
  team2Color: string;
  team2Logo: string;
  inPoint: number;
  outPoint: number;
  thumbnailPath: string;
  skimPath: string;
  lowresPath: string;
  dateOfCreated: string;
  dateOfModified: string;
  interviewPartialFilename: string;
  highlightPartialFilename: string;

  // assetUrlBuilder: AssetUrlBuilder;
  timeCode: TimeCode;
  _symbolicPath: string | undefined;

  constructor(data: WkblVideoVOProps) {
    super(data);
    this.id = data.ID;
    this.refId = data.REF_ID;
    this.type = data.TYPE;
    this.subType = data.SUB_TYPE;
    this.title = data.TITLE;
    this.categoryId = data.CATEGORY_ID;
    this.mediaFormat = data.MEDIA_FORMAT;
    this.mediaFps = data.MEDIA_FPS;
    this.mediaFileSize = data.MEDIA_FILE_SIZE;
    this.durationOfClip = data.DURATION_OF_CLIP;
    this.season = data.SEASON;
    this.gameType = data.GAME_TYPE;
    this.gameDate = data.GAME_DATE;
    this.videoType = data.VIDEO_TYPE;
    this.team1 = data.TEAM_1;
    this.team1Name = data.TEAM_1_NAME;
    this.team1Color = data.TEAM_1_COLOR;
    this.team1Logo = data.TEAM_1_LOGO;
    this.team2 = data.TEAM_2;
    this.team2Name = data.TEAM_2_NAME;
    this.team2Color = data.TEAM_2_COLOR;
    this.team2Logo = data.TEAM_2_LOGO;
    this.inPoint = data.IN_POINT;
    this.outPoint = data.OUT_POINT;
    this.thumbnailPath = data.THUMBNAIL_PATH;
    this.skimPath = data.SKIM_PATH;
    this.lowresPath = data.LOWRES_PATH;
    this.dateOfCreated = data.DATE_OF_CREATED;
    this.dateOfModified = data.DATE_OF_MODIFIED;
    this.timeCode = new TimeCode(this.mediaFps);
    this.interviewPartialFilename = data.INTERVIEW_PARTIAL_FILENAME;
    this.highlightPartialFilename = data.HIGHLIGHT_PARTIAL_FILENAME;
  }

  get symbolicPath(): string {
    return this._symbolicPath || DEFAULT_SYMBOLIC_PATH;
  }

  set symbolicPath(symbolicPath: string) {
    this._symbolicPath = symbolicPath;
  }

  get videoLowresRelativeURL() {
    return new LowresRelativeURLBuilder()
      .categoryId(this.categoryId)
      .fileName(this.lowresPath)
      .build();
  }

  get videoThumbnailRelativeURL() {
    return new ThumbnailRelativeURLBuilder()
      .categoryId(this.categoryId)
      .fileName(this.thumbnailPath)
      .build();
  }

  get scriptThumbnailRelativeURL() {
    return new ScriptThumbnailRelativeURLBuilder()
      .categoryId(this.categoryId)
      .refId(this.refId)
      .fileName(this.thumbnailPath)
      .build();
  }

  get skimRelativeURL() {
    return new SkimRelativeURLBuilder().categoryId(this.categoryId).fileName(this.skimPath).build();
  }

  getLowresCloudfrontPath() {
    return `${HostFactory.cloudfrontURL}/${this.videoLowresRelativeURL}`;
  }

  getThumbnailFilePath(symbolicPath: string) {
    const url = `${STORAGE_URL}${symbolicPath}/${this.videoThumbnailRelativeURL}`;
    return assetWithToken(url);
  }

  getThumbnailCloudfrontPath() {
    const url = `${HostFactory.cloudfrontURL}/${this.videoThumbnailRelativeURL}`;
    return url;
  }

  getScriptThumbnailFilePath(symbolicPath: string) {
    const url = `${STORAGE_URL}${symbolicPath}/${this.scriptThumbnailRelativeURL}`;
    return assetWithToken(url);
  }

  getScriptCloudfrontPath() {
    const url = `${HostFactory.cloudfrontURL}/${this.scriptThumbnailRelativeURL}`;
    return url;
  }

  getSkimFilePath(symbolicPath: string) {
    const url = `${STORAGE_URL}${symbolicPath}/${this.skimRelativeURL}`;
    return assetWithToken(url);
  }

  getSkimCloudfrontPath() {
    const url = `${HostFactory.cloudfrontURL}/${this.skimRelativeURL}`;
    return url;
  }

  get formattedGameDate() {
    return formattedDateString(this.gameDate)?.split(' ')[0];
  }

  getTeam1LogoPath(symbolicPath: string) {
    const url = `${STORAGE_URL}${symbolicPath}/${SystemFolder.LOGO_NAME}/${this.team1Logo}`;
    return assetWithToken(url);
  }

  getTeam2LogoPath(symbolicPath: string) {
    const url = `${STORAGE_URL}${symbolicPath}/${SystemFolder.LOGO_NAME}/${this.team2Logo}`;
    return assetWithToken(url);
  }

  getTeamLogoPath() {
    const { symbolicPath } = this;
    return {
      team1: {
        nas: this.getTeam1LogoPath(symbolicPath),
        cloudfront: `${HostFactory.cloudfrontURL}/${SystemFolder.LOGO_NAME}/${this.team1Logo}`,
        db: assetWithOutToken(`${HostFactory.apiURL}${API.TEAM_LOGO}/${this.team1}`),
      },
      team2: {
        nas: this.getTeam2LogoPath(symbolicPath),
        cloudfront: `${HostFactory.cloudfrontURL}/${SystemFolder.LOGO_NAME}/${this.team2Logo}`,
        db: assetWithOutToken(`${HostFactory.apiURL}${API.TEAM_LOGO}/${this.team2}`),
      },
    };
  }

  getFilePath() {
    const { symbolicPath } = this;

    const nasThumbnail = this.isVideo
      ? this.getThumbnailFilePath(symbolicPath)
      : this.getScriptThumbnailFilePath(symbolicPath);

    const cloudfrontThumbnail = this.isVideo
      ? this.getThumbnailCloudfrontPath()
      : this.getScriptCloudfrontPath();

    const nasSkim = this.getSkimFilePath(symbolicPath);
    const cloudfrontSkim = this.getSkimCloudfrontPath();

    const cloudfrontLowres = this.getLowresCloudfrontPath();

    return {
      thumbnail: {
        nas: nasThumbnail,
        cloudfront: cloudfrontThumbnail,
      },
      skim: {
        nas: nasSkim,
        cloudfront: cloudfrontSkim,
      },
      lowres: {
        cloudfront: cloudfrontLowres,
      },
    };
  }

  getBackgroundImage(isShow: boolean) {
    const { thumbnail, skim } = this.getFilePath();
    return isShow
      ? `url(${skim.cloudfront}), url(${skim.nas}), url(${thumbnail.cloudfront}), url(${thumbnail.nas}), url(${defaultThumbnail})`
      : `url(${thumbnail.cloudfront}), url(${thumbnail.nas}), url(${defaultThumbnail})`;
  }

  get isVideo(): boolean {
    return this.type === WKBLVideoContentsType.VIDEO;
  }

  isShowTeam(config: ConfigVO) {
    let result = true;

    const { forcedHideTeam, forcedShowTeam } = config;
    const { team1, team2, gameType } = this;

    if (!forcedHideTeam || !forcedShowTeam) {
      return result;
    }

    const isForcedHide = forcedHideTeam.findIndex(id => id === team1 || id === team2) !== -1;
    const isForcedShow = forcedShowTeam.findIndex(id => id === gameType) !== -1;

    if (isForcedHide && !isForcedShow) {
      result = false;
    }

    return result;
  }
}

export default WkblVideoVO;
